import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Gamepad2, Users, BarChart3, UserCog, QrCode, Copy, ExternalLink, Sparkles, ShieldCheck, Share2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Kpi } from "@/components/Kpi";
import { PlaysTimeseries, CtaBar } from "@/components/Charts";
import PerGameTable from "@/components/PerGameTable";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GamesPanel, ParticipantsPanel } from "@/pages/AdminDashboard";
import MerchantProfile from "@/pages/MerchantProfile";
import QRCodeDialog from "@/components/QRCodeDialog";
import RgpdContractDialog from "@/components/RgpdContractDialog";
import StatusPill from "@/components/StatusPill";
import { QRCodeSVG } from "qrcode.react";
import { formatDate } from "@/lib/format";
import LegalFooter from "@/components/LegalFooter";

export default function MerchantDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [rgpdOpen, setRgpdOpen] = useState(false);
  const [rgpdReadOnly, setRgpdReadOnly] = useState(false);

  useEffect(() => {
    api.get("/merchant/me").then((r) => {
      setMe(r.data);
      if (!r.data?.rgpd_accepted_at) {
        // First login → force acceptance
        setRgpdReadOnly(false);
        setRgpdOpen(true);
      }
    }).catch(() => {});
  }, []);

  function openContract() {
    setRgpdReadOnly(Boolean(me?.rgpd_accepted_at));
    setRgpdOpen(true);
  }

  const tabs = [
    { id: "overview", label: t("dashboard.overview"), Icon: LayoutDashboard },
    { id: "games", label: t("dashboard.games"), Icon: Gamepad2 },
    { id: "participants", label: t("dashboard.participants"), Icon: Users },
    { id: "analytics", label: t("dashboard.analytics"), Icon: BarChart3 },
    { id: "profile", label: t("profile.title"), Icon: UserCog },
  ];
  return (
    <div className="min-h-screen bg-muted/40">
      <Navbar variant="app" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {t("dashboard.role_merchant")}
            </div>
            <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("dashboard.welcome")}, {user?.name?.split(" ")[0]}
            </h1>
          </div>
          <button
            type="button"
            onClick={openContract}
            data-testid="open-rgpd-contract-btn"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {t("rgpd.open_link")}
          </button>
        </div>

        <div className="flex items-center gap-1 p-1 bg-card border border-border rounded-2xl mb-6 overflow-x-auto">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`merchant-tab-${id}`}
              className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === id
                  ? "bg-foreground text-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <MOverview />}
        {tab === "games" && <GamesPanel role="merchant" onNavigate={setTab} />}
        {tab === "participants" && <ParticipantsPanel />}
        {tab === "analytics" && <MAnalytics />}
        {tab === "profile" && (
          <MerchantProfile
            onRgpdOpen={openContract}
            rgpdAccepted={Boolean(me?.rgpd_accepted_at)}
          />
        )}

        <LegalFooter className="mt-10 pt-6 border-t border-border/40" />
      </div>

      <RgpdContractDialog
        open={rgpdOpen}
        onOpenChange={setRgpdOpen}
        merchant={me}
        requireAcceptance={!rgpdReadOnly && !me?.rgpd_accepted_at}
        onAccepted={(data) => {
          setMe((prev) =>
            prev
              ? {
                  ...prev,
                  rgpd_accepted_at: data.rgpd_accepted_at,
                  rgpd_accepted_version: data.rgpd_accepted_version,
                }
              : prev,
          );
        }}
      />
    </div>
  );
}

function MOverview() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [perGame, setPerGame] = useState([]);
  const [me, setMe] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data));
    api.get("/analytics/per-game").then((r) => setPerGame(r.data.games));
    api.get("/merchant/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-muted-foreground">{t("dashboard.loading")}</div>;
  const merchantUrl = me?.slug
    ? `${window.location.origin}/m/${me.slug}`
    : null;
  const shareUrl = me?.slug
    ? `${window.location.origin}/api/share/m/${me.slug}`
    : null;
  return (
    <div className="space-y-6">
      {merchantUrl && (
        <MerchantQrHero
          me={me}
          url={merchantUrl}
          shareUrl={shareUrl}
          onOpen={() => setQrOpen(true)}
        />
      )}
      <QRCodeDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        url={merchantUrl || ""}
        fileName={me?.slug ? `qrise-${me.slug}` : "qrise-merchant"}
        title={t("dashboard.permanent_qr_title")}
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label={t("dashboard.kpi_plays")}
          value={data.total_plays}
          testId="kpi-plays"
        />
        <Kpi
          label={t("dashboard.kpi_leads")}
          value={data.total_leads}
          testId="kpi-leads"
        />
        <Kpi
          label={t("dashboard.kpi_conversion")}
          value={`${data.conversion_rate}%`}
          testId="kpi-conversion"
        />
        <Kpi
          label={t("dashboard.kpi_games")}
          value={data.active_games ?? data.total_games}
          testId="kpi-games"
        />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-6">
            {t("dashboard.timeseries")}
          </div>
          <PlaysTimeseries data={data.timeseries} />
        </div>
        <div className="qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-6">
            {t("dashboard.cta_performance")}
          </div>
          {data.cta_stats.length ? (
            <CtaBar data={data.cta_stats} />
          ) : (
            <div className="text-sm text-muted-foreground">
              {t("dashboard.no_data")}
            </div>
          )}
        </div>
      </div>
      <div className="qrise-card p-6">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
          {t("dashboard.per_game")}
        </div>
        {perGame.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("dashboard.th_game")}</TableHead>
                <TableHead>{t("dashboard.th_status")}</TableHead>
                <TableHead>{t("dashboard.th_dates")}</TableHead>
                <TableHead className="text-right">{t("dashboard.th_plays")}</TableHead>
                <TableHead className="text-right">{t("dashboard.th_wins")}</TableHead>
                <TableHead className="text-right">{t("dashboard.th_conversion")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perGame.map((g) => (
                <TableRow key={g.game_id}>
                  <TableCell className="font-medium">{g.title}</TableCell>
                  <TableCell>
                    {g.status ? (
                      <StatusPill status={g.status} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {g.start_date || g.end_date ? (
                      <>
                        {formatDate(g.start_date) || "…"}
                        <span className="opacity-60 mx-1">→</span>
                        {formatDate(g.end_date) || "…"}
                      </>
                    ) : (
                      <span className="italic">{t("dashboard.no_dates")}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{g.plays}</TableCell>
                  <TableCell className="text-right">{g.wins}</TableCell>
                  <TableCell className="text-right">{g.conversion}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-sm text-muted-foreground">
            {t("dashboard.no_data")}
          </div>
        )}
      </div>
    </div>
  );
}

function MerchantQrHero({ me, url, shareUrl, onOpen }) {
  const { t } = useTranslation();
  const active = me?.active_game;
  const company = me?.company || me?.name || "";

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("dashboard.copied"));
    } catch (e) {
      toast.error(t("dashboard.copy_error"));
    }
  }

  async function copyShare() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("dashboard.copied"));
    } catch (e) {
      toast.error(t("dashboard.copy_error"));
    }
  }

  return (
    <div
      className="relative qrise-card p-6 sm:p-8 overflow-hidden"
      data-testid="merchant-qr-hero"
    >
      {/* Ambient radial glow */}
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, #8B5CF6 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, #38bdf8 0%, transparent 70%)",
        }}
      />

      <div className="relative grid gap-8 md:grid-cols-[auto_1fr] items-center">
        {/* QR visual */}
        <button
          type="button"
          onClick={onOpen}
          className="group relative rounded-3xl bg-white p-4 shadow-[0_30px_80px_-25px_rgba(139,92,246,0.45)] border border-white/60 transition-transform hover:scale-[1.02] active:scale-[0.99]"
          data-testid="merchant-qr-visual"
          title={url}
        >
          <QRCodeSVG
            value={url}
            size={180}
            level="H"
            marginSize={1}
            fgColor="#0f172a"
            bgColor="#ffffff"
          />
          <div className="absolute inset-0 rounded-3xl ring-0 ring-violet-500/0 group-hover:ring-4 group-hover:ring-violet-500/20 transition-all pointer-events-none" />
        </button>

        {/* Details */}
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-bold text-violet-600 dark:text-violet-300">
            <Sparkles className="h-3.5 w-3.5" />
            {t("dashboard.permanent_qr_badge")}
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">
            {t("dashboard.permanent_qr_title")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-xl">
            {t("dashboard.permanent_qr_body", {
              brand: company || t("dashboard.your_brand"),
            })}
          </p>

          <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-muted/60 px-3 py-2 max-w-lg">
            <span
              className="font-mono text-xs text-muted-foreground truncate flex-1"
              data-testid="merchant-qr-url"
              title={url}
            >
              {url}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={copy}
              className="h-7 px-2 text-xs"
              data-testid="merchant-qr-copy-btn"
            >
              <Copy className="h-3 w-3 mr-1" />
              {t("dashboard.copy_link")}
            </Button>
          </div>

          {shareUrl && (
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-violet-500/25 bg-violet-500/5 px-3 py-2 max-w-lg">
              <Share2 className="h-3.5 w-3.5 shrink-0 text-violet-500" />
              <span
                className="text-xs text-muted-foreground truncate flex-1"
                data-testid="merchant-share-url-hint"
                title={shareUrl}
              >
                {t(
                  "dashboard.share_link_hint",
                  "Pour WhatsApp & réseaux sociaux (aperçu avec image)"
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={copyShare}
                className="h-7 px-2 text-xs text-violet-600 dark:text-violet-300 hover:text-violet-700"
                data-testid="merchant-share-copy-btn"
              >
                <Copy className="h-3 w-3 mr-1" />
                {t("dashboard.copy_share_link", "Copier le lien réseaux")}
              </Button>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              className="h-10 rounded-xl bg-foreground text-background hover:bg-foreground/90"
              onClick={onOpen}
              data-testid="merchant-qr-open-btn"
            >
              <QrCode className="h-4 w-4 mr-1.5" />
              {t("dashboard.download_qr")}
            </Button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl text-sm text-muted-foreground hover:text-foreground transition"
              data-testid="merchant-qr-preview-link"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t("dashboard.preview_url")}
            </a>
          </div>

          {/* Active game status */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {active ? (
              <>
                <StatusPill status={active.status || "live"} />
                <span className="text-xs text-muted-foreground">
                  {t("dashboard.active_game_label", { title: active.title })}
                </span>
                {(active.start_date || active.end_date) && (
                  <span
                    className="text-[11px] font-mono text-muted-foreground"
                    data-testid="merchant-qr-active-dates"
                  >
                    {formatDate(active.start_date) || "…"}
                    <span className="opacity-60 mx-1">→</span>
                    {formatDate(active.end_date) || "…"}
                  </span>
                )}
              </>
            ) : (
              <span
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold bg-muted text-muted-foreground"
                data-testid="merchant-qr-active-badge"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                {t("dashboard.no_active_game")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MAnalytics() {
  return <AnalyticsDashboard />;
}
